/**
 * LiveTechChip — one row in the dispatcher's "Live Techs" strip.
 * Renders a single tech's name, current GPS state, time-in-state, and
 * weekly hours-vs-budget indicator. Color/dot conventions mirror the
 * per-tech sidebar GPS toggle so the dispatcher sees the whole fleet
 * at a glance.
 *
 * Extracted from `pages/Dispatch.jsx` to slim the file (was 2,549 lines
 * before — this extract drops it by ~45 lines).
 */

const STATE_COPY = {
  at_site: { color: "bg-green-100 text-green-800 border-green-200", label: "On Site", dot: "bg-green-500" },
  en_route: { color: "bg-blue-100 text-blue-800 border-blue-200", label: "En Route", dot: "bg-blue-500" },
  en_route_shop: { color: "bg-blue-100 text-blue-800 border-blue-200", label: "En route · Shop", dot: "bg-blue-500" },
  at_shop: { color: "bg-indigo-100 text-indigo-800 border-indigo-200", label: "At Shop", dot: "bg-indigo-500" },
  stationary_off_site: { color: "bg-slate-100 text-slate-600 border-slate-200", label: "Off Site", dot: "bg-slate-400" },
  offsite: { color: "bg-slate-100 text-slate-600 border-slate-200", label: "Off Site", dot: "bg-slate-400" },
  offline: { color: "bg-slate-100 text-slate-500 border-slate-200", label: "No Signal", dot: "bg-slate-300" },
};

const SHORT = { at_site: "On site", en_route: "En route", en_route_shop: "To shop", at_shop: "At shop", stationary_off_site: "Off site", offsite: "Off site", offline: "No signal" };
const mins = (m) => (m == null ? "" : m < 60 ? `${Math.max(1, Math.round(m))}m` : `${(m / 60).toFixed(1)}h`);

// One-line live GPS state for the tech's row label on the dispatch board.
export function TechLiveState({ live }) {
  if (!live) return null;
  const s = STATE_COPY[live.state] || STATE_COPY.offline;
  const stale = live.signal_stale && live.state !== "offline";
  const label = stale ? "Stale" : (SHORT[live.state] || SHORT.offline);
  const detail = live.state === "at_site" && live.site_name ? live.site_name : mins(live.mins_in_state);
  const tip = [s.label, live.site_name, live.mins_in_state != null ? `${mins(live.mins_in_state)} in state` : null, live.mins_since_ping != null ? `last ping ${Math.round(live.mins_since_ping)}m ago` : null].filter(Boolean).join(" · ");
  return (
    <div className={`flex items-center gap-1 text-[8px] leading-tight truncate ${stale ? "text-amber-600" : "text-slate-500"}`} title={tip} data-testid={`tech-live-${live.tech_id}`} data-state={live.state}>
      <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${stale ? "bg-amber-400" : s.dot}`} />
      <span className="font-semibold shrink-0">{label}</span>
      {detail && <span className="truncate opacity-80">· {detail}</span>}
    </div>
  );
}

export function LiveTechChip({ tech }) {
  const s = STATE_COPY[tech.state] || STATE_COPY.offline;
  const timeLabel = tech.mins_in_state != null
    ? (tech.mins_in_state < 60 ? `${Math.max(1, Math.round(tech.mins_in_state))}m` : `${(tech.mins_in_state / 60).toFixed(1)}h`)
    : "—";
  const stale = tech.signal_stale;
  // Weekly hours vs per-tech budget. Uses tech-entered logged_hours, not GPS
  // auto-calc. Over budget = red; at/under = green.
  const wkUsed = tech.weekly_hours_used;
  const wkBudget = tech.weekly_hours_budget;
  const overBudget = wkUsed != null && wkBudget != null && wkUsed > wkBudget;
  return (
    <div className={`inline-flex items-center gap-2 px-2.5 py-1.5 rounded-full border text-[11px] ${s.color}`} data-testid={`live-tech-${tech.tech_id}`}>
      <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: tech.color }} />
      <span className="font-semibold">{tech.name}</span>
      <span className={`w-1.5 h-1.5 rounded-full ${s.dot} shrink-0`} />
      <span className="text-[10px]">
        {s.label}
        {tech.state === "at_site" && tech.site_name ? ` · ${tech.site_name}` : ""}
      </span>
      <span className="text-[9px] opacity-70">{timeLabel}</span>
      {wkUsed != null && wkBudget != null && (
        <span
          className={`text-[9px] font-semibold px-1.5 py-0.5 rounded ${overBudget ? "bg-red-100 text-red-700 border border-red-200" : "bg-green-50 text-green-700 border border-green-200"}`}
          data-testid={`live-tech-hours-${tech.tech_id}`}
          title={`Week ${wkUsed}h / ${wkBudget}h budget`}>
          {wkUsed}h / {wkBudget}h
        </span>
      )}
      {stale && <span className="text-[9px] font-semibold text-amber-700 bg-amber-50 px-1 py-0.5 rounded" title={`Last ping ${tech.mins_since_ping}m ago`}>stale</span>}
    </div>
  );
}
